// 고객 요청 배지·리스트 줄·공정 삭제 안내 — 간트 응답의 집계 3필드(requestCount·unsentCount·appliedCount)를 화면 문구로 바꾸는 순수 함수.
// 세 필드는 같이 숫자이거나 같이 null(집계 조회 실패)이다. null 이면 배지·줄을 생략하고 오류는 표시하지 않는다.

export type CustReqCounts = {
  requestCount?: number | null
  unsentCount?: number | null
  appliedCount?: number | null
}

const isCount = (n: number | null | undefined): n is number => typeof n === 'number' && Number.isFinite(n)

/** 배지 표시 여부 — 숫자이고 0 초과일 때만 */
export function showBadge(requestCount: number | null | undefined): boolean {
  return isCount(requestCount) && requestCount > 0
}

/** 미전달 여부 — 미전달 건수가 1 이상 */
export function hasUnsent(unsentCount: number | null | undefined): boolean {
  return isCount(unsentCount) && unsentCount > 0
}

/** 배지 문구 */
export function badgeText(requestCount: number): string {
  return `요청 ${requestCount}`
}

/** 접근성·hover 문구 */
export function badgeAriaLabel(requestCount: number, unsentCount: number | null | undefined): string {
  return `요청 ${requestCount}건, 미전달 ${isCount(unsentCount) ? unsentCount : 0}건`
}

/** 리스트 펼침 줄 문구 — 집계 실패(null)면 null(줄 생략), 0건이면 '없음' */
export function listLineText(c: CustReqCounts): string | null {
  if (!isCount(c.requestCount)) return null
  if (c.requestCount === 0) return '없음'
  return `${c.requestCount}건 · 반영 ${isCount(c.appliedCount) ? c.appliedCount : 0} · 미전달 ${isCount(c.unsentCount) ? c.unsentCount : 0}`
}

/** 여러 공정의 합계 — 하나라도 숫자가 있으면 숫자 항목만 더하고, 모두 null 이면 세 필드 모두 null */
export function sumCounts(items: readonly CustReqCounts[]): Required<CustReqCounts> {
  const known = items.filter(i => isCount(i.requestCount))
  if (known.length === 0) return { requestCount: null, unsentCount: null, appliedCount: null }
  const add = (pick: (c: CustReqCounts) => number | null | undefined) => known.reduce((s, c) => s + (isCount(pick(c)) ? (pick(c) as number) : 0), 0)
  return { requestCount: add(c => c.requestCount), unsentCount: add(c => c.unsentCount), appliedCount: add(c => c.appliedCount) }
}

/** 접힌 구간 행에서 배지를 누를 때 상세창을 열 공정 — 미전달 있는 공정 우선 → 요청 있는 첫 공정 → 대표(첫) 공정 */
export function pickBadgeTarget<T extends CustReqCounts & { taskId: number }>(bars: readonly T[]): number | null {
  if (bars.length === 0) return null
  const unsent = bars.find(b => hasUnsent(b.unsentCount))
  if (unsent) return unsent.taskId
  const withReq = bars.find(b => showBadge(b.requestCount))
  return (withReq ?? bars[0]).taskId
}

/** 공정 삭제 확인창 문구. requestCount 가 1 이상이면 고객 요청 안내를 덧붙이고, 0·null 이면 기존 문구 그대로 둔다(삭제는 막지 않음). */
export function buildTaskDeleteConfirm(opts: { multiCount?: number; requestCount: number | null | undefined }): string {
  const multi = opts.multiCount !== undefined
  const head = multi ? `선택한 ${opts.multiCount}개 공종을 삭제하시겠습니까?` : '이 공종을 삭제하시겠습니까?'
  if (!showBadge(opts.requestCount)) return head
  const subject = multi ? '선택한 공종을 삭제하면' : '이 공종을 삭제하면'
  return `${head}\n\n${subject} 반장 배정, 발주, 고객 요청 ${opts.requestCount}건이 함께 보이지 않게 됩니다. 같은 이름으로 다시 추가해도 이어지지 않습니다.`
}
